'use client';

import { createPortal } from 'react-dom';
import { Pin } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import classnames from 'classnames';
import { PopoverBox } from './PopoverBox';
import { PopoverStoreContext } from './PopoverContext';
import { createPopoverStore, type PopoverState, type PopoverStore } from './popover-store';
import styles from './Popover.module.scss';

interface PopoverProviderProps {
    children: ReactNode;
}

/** Holds the app's one shared hover popover (see `usePopover`) and draws it. */
export function PopoverProvider({ children }: PopoverProviderProps): JSX.Element {
    // Created once: its identity is the context's value, so it must stay stable.
    const [store] = useState(createPopoverStore);
    const state = useSyncExternalStore(store.subscribe, store.getState);
    const portalRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (portalRef.current) {
            return;
        }

        portalRef.current = document.createElement('div');
        portalRef.current.id = 'popover-portal-root';
        document.body.appendChild(portalRef.current);

        return (): void => {
            if (portalRef.current?.parentNode === document.body) {
                document.body.removeChild(portalRef.current);
            }
            portalRef.current = null;
        };
    }, []);

    return (
        <PopoverStoreContext.Provider value={store}>
            {children}
            {portalRef.current !== null && state.isVisible && createPortal(
                <SharedPopover state={state} store={store} />,
                portalRef.current,
            )}
        </PopoverStoreContext.Provider>
    );
}

interface SharedPopoverProps {
    state: PopoverState;
    store: PopoverStore;
}

/** The shared popover while it shows: a tooltip that describes its trigger. */
function SharedPopover({ state, store }: SharedPopoverProps): JSX.Element {
    const { triggerElement, isPinned, isPinnable } = state;
    const id = useId();
    const getAnchor = useCallback((): HTMLElement | undefined => triggerElement, [triggerElement]);
    const handleOutsidePointerDown = useCallback((): void => {
        if (triggerElement !== undefined) {
            store.hide(triggerElement);
        }
    }, [store, triggerElement]);

    // The trigger is described by the popover while it shows.
    useEffect(() => {
        if (triggerElement === undefined) {
            return undefined;
        }
        triggerElement.setAttribute('aria-describedby', id);
        return (): void => {
            triggerElement.removeAttribute('aria-describedby');
        };
    }, [id, triggerElement]);

    return (
        <PopoverBox
            id={id}
            role="tooltip"
            getAnchor={getAnchor}
            placement={state.placement}
            onOutsidePointerDown={handleOutsidePointerDown}
            onEscape={store.unpin}
            // A hover popover only shows; the pointer passes through it, so
            // one that opens under the cursor never steals its trigger's hover.
            isPassThrough={!isPinnable}
        >
            <div className={styles.content}>{state.content}</div>
            {isPinnable && triggerElement !== undefined && (
                <button
                    type="button"
                    className={classnames(styles.pinButton, isPinned && styles.pinButtonActive)}
                    aria-label={isPinned ? 'Unpin popover' : 'Pin popover'}
                    onClick={isPinned ? store.unpin : (): void => store.pin(triggerElement)}
                >
                    <Pin
                        size={10}
                        strokeWidth={2}
                        aria-hidden="true"
                        style={isPinned ? { fill: 'currentColor' } : undefined}
                    />
                </button>
            )}
        </PopoverBox>
    );
}
